import React from 'react';

/** Rotating color palette for {{placeholder}} highlights: [foreground, background]. */
export const MAPPING_COLORS: [string, string][] = [
  ['#fbbf24', 'rgba(251,191,36,0.12)'],
  ['#38bdf8', 'rgba(56,189,248,0.12)'],
  ['#a78bfa', 'rgba(167,139,250,0.12)'],
  ['#34d399', 'rgba(52,211,153,0.12)'],
  ['#fb7185', 'rgba(251,113,133,0.12)'],
  ['#fb923c', 'rgba(251,146,60,0.12)'],
  ['#22d3ee', 'rgba(34,211,238,0.12)'],
  ['#e879f9', 'rgba(232,121,249,0.12)'],
];

/** Escape only &, <, > for safe HTML rendering. Do NOT escape " — it must stay as 1 char for textarea sync. */
export const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// JSON token colors
const JSON_KEY = '#7dd3fc';       // sky-300
const JSON_STRING = '#6ee7b7';    // emerald-300
const JSON_NUMBER = '#fdba74';    // orange-300
const JSON_BOOL = '#c4b5fd';      // violet-300
const JSON_STRUCT = '#71717a';    // zinc-500

/**
 * Detect whether a line is inside a JSON block by tracking brace/bracket depth.
 * Operates on raw escaped text (before any span tags are inserted).
 */
const detectJsonLines = (lines: string[]): boolean[] => {
  const result: boolean[] = new Array(lines.length).fill(false);
  let depth = 0;
  let blockStart = -1;

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();

    if (depth === 0 && (trimmed.startsWith('{') || trimmed.startsWith('['))) {
      blockStart = i;
    }

    for (const ch of trimmed) {
      if (ch === '{' || ch === '[') depth++;
      else if (ch === '}' || ch === ']') depth = Math.max(0, depth - 1);
    }

    if (blockStart >= 0) {
      result[i] = true;
    }

    if (depth === 0 && blockStart >= 0) {
      blockStart = -1;
    }
  }

  return result;
};

/**
 * Apply JSON syntax coloring to a line of escaped HTML text.
 * MUST be called before placeholder highlighting to avoid matching inside span attributes.
 */
const colorizeJsonLine = (line: string): string => {
  // Color JSON string keys: "key":
  let result = line.replace(
    /"([\w\-.$]+)"\s*:/g,
    `<span style="color:${JSON_KEY}">"$1"</span>:`
  );

  // Color string values after colon: "value"
  result = result.replace(
    /:\s*"([^"]*)"/g,
    (_, val) => `: <span style="color:${JSON_STRING}">"${val}"</span>`
  );

  // Color standalone string values in arrays (after [ or ,)
  result = result.replace(
    /(?<=[[,]\s*)"([^"]*)"/g,
    `<span style="color:${JSON_STRING}">"$1"</span>`
  );

  // Color numbers (after colon, before comma/bracket/end)
  result = result.replace(
    /(?<=:\s*)(-?\d+\.?\d*)(?=[,\s\]}]|$)/g,
    `<span style="color:${JSON_NUMBER}">$1</span>`
  );

  // Color booleans and null
  result = result.replace(
    /\b(true|false|null)\b/g,
    `<span style="color:${JSON_BOOL}">$1</span>`
  );

  // Color braces/brackets only when they are NOT inside an HTML tag
  // (match { } [ ] that are not preceded by a quote or angle bracket)
  result = result.replace(
    /(?<![="<\w])([{}[\]])(?![">])/g,
    `<span style="color:${JSON_STRUCT}">$1</span>`
  );

  return result;
};

/**
 * Build syntax-highlighted HTML for the prompt editor overlay.
 *
 * Order matters: JSON colorization runs first on raw escaped text,
 * then placeholder highlighting runs on the result. This prevents
 * the JSON colorizer from matching content inside placeholder span attributes.
 */
export const buildHighlightedHtml = (content: string): string => {
  const escaped = escapeHtml(content);
  const lines = escaped.split('\n');
  const jsonFlags = detectJsonLines(lines);

  const colorMap = new Map<string, [string, string]>();
  let colorIndex = 0;

  const highlightedLines = lines.map((line, i) => {
    // Step 1: JSON coloring (on clean escaped text, no spans yet)
    let result = jsonFlags[i] ? colorizeJsonLine(line) : line;

    // Step 2: Placeholder highlighting (runs after JSON spans are in place,
    // but {{word}} patterns don't appear inside span attributes, so this is safe)
    result = result.replace(/\{\{(\w+)\}\}/g, (match, name) => {
      if (!colorMap.has(name)) colorMap.set(name, MAPPING_COLORS[colorIndex++ % MAPPING_COLORS.length]);
      const [fg, bg] = colorMap.get(name)!;
      return `<span style="color:${fg};background:${bg};border-radius:3px;padding:0 2px">${match}</span>`;
    });

    return result;
  });

  return highlightedLines.join('\n');
};

/** Shared monospace editor style for the textarea + highlight overlay. */
export const editorStyle: React.CSSProperties = {
  fontFamily: "'JetBrains Mono','Fira Code','Consolas',monospace",
  fontSize: 13,
  lineHeight: '1.65',
  padding: '10px 12px',
  margin: 0,
  border: 'none',
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
};
